'use client';

import React, { useEffect, useState, FC } from 'react';
import CommentAvatar from '@/components/(postTypes)/platforms/(archive)/layout/(review)/list/CommentAvatar';
import { formatCommentDate } from '@/utils/post/comments/formatCommentDate';
import LikeDislike from '@/components/(postTypes)/platforms/(archive)/layout/(review)/list/LikeDislike';
import { renderStars } from '@/utils/post/comments/renderStars';
import Reply from '@/components/(postTypes)/platforms/(archive)/layout/(review)/list/Reply';
import Link from 'next/link';
import clsx from 'clsx';
import { BrokerNode } from '@/stores/selectBrokers/store';
import { safeHtmlLinks } from '@/utils/links/safeHref';

/*
 * Price predictions left with an old version of the form (symbol pages, Mehr 1404) were stored
 * without their labels: "<p> ۱۱۶٬۶۶۱ تومان.<br />\n:   .,reason</p>", shown as a bare number, a
 * stray "." and a line starting with ":". Shown again with labels; the stored text is untouched.
 */
const LEGACY_PREDICTION = /^\s*<p>\s*([0-9۰-۹٬,.]+)\s*تومان\s*\.?\s*<br\s*\/?>\s*:[\s.,،]*/;
const fixLegacyPrediction = (html: string) =>
    LEGACY_PREDICTION.test(html)
        ? html.replace(
              LEGACY_PREDICTION,
              (_m, price: string) =>
                  `<p><strong>پیش‌بینی قیمت:</strong> ${price} تومان<br /><strong>دلیل:</strong> `,
          )
        : html;

// --- SECURITY FIREWALL ---
// Cleans raw HTML from the database before it is injected into the DOM
const sanitizeOutput = (html: string) => {
    if (!html) return '';

    // 1. Destroy <script> tags and all code inside them
    let safeHtml = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

    // 2. Destroy <iframe> tags (stops hackers from embedding external malicious pages)
    safeHtml = safeHtml.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');

    // 3. Strip out <a> tags (removes the link entirely, but keeps the readable text inside)
    safeHtml = safeHtml.replace(/<\/?a[^>]*>/gi, '');

    // 4. Strip dangerous inline event handlers (e.g., onclick="stealCookies()")
    safeHtml = safeHtml.replace(/ on\w+="[^"]*"/gi, '');
    safeHtml = safeHtml.replace(/ on\w+='[^']*'/gi, '');
    safeHtml = safeHtml.replace(/ on\w+=[^\s>]+/gi, '');

    return safeHtml;
};
// -------------------------

/** About five lines on a phone. */
const LONG_COMMENT_CHARS = 280;

interface CommentAuthorNode {
    name: string;
}

export interface CommentAuthor {
    node: CommentAuthorNode;
    roles: string[];
}

interface Comment {
    authorRoles?: string[];
    author?: CommentAuthor;
    date?: string;
    rate?: number;
    content?: string;
    databaseId: number;
    likes?: number;
    dislikes?: number;
}

interface CommentProps {
    comment: Comment;
    broker?: BrokerNode;
}

const Comment: FC<CommentProps> = ({ comment, broker }) => {
    const [brokerData, setBrokerData] = useState<BrokerNode['brokerSingle'] | null>(null);
    const [expanded, setExpanded] = useState(false);

    const safeContent = fixLegacyPrediction(sanitizeOutput(comment?.content || ''));
    // Long texts are shortened on screen only (all of it stays in the HTML). Decided from the
    // text length, so the server already renders the «ادامه» button and nothing jumps later.
    const isLong = safeContent.replace(/<[^>]+>/g, '').trim().length > LONG_COMMENT_CHARS;

    const isAdmin = (roles: string[]): boolean => {
        return roles.includes('administrator') || roles.includes('editor');
    };

    useEffect(() => {
        if (broker) {
            setBrokerData(broker?.brokerSingle);
        }
    }, [broker]);

    return (
        <>
            <div>
                <div className='flex items-start justify-between'>
                    <div className={'flex flex-col justify-start gap-2'}>
                        <div className={'inline-flex items-center justify-center gap-2'}>
                            <CommentAvatar
                                admin={isAdmin(comment?.authorRoles || [])}
                                className={
                                    'size-9 shrink-0 rounded-full border dark:border-white/30'
                                }
                            />
                            <div className={'flex flex-col gap-y-1 justify-start'}>
                                <div className={'text-xs font-medium'}>
                                    {isAdmin(comment.authorRoles || [])
                                        ? 'خانه سرمایه'
                                        : comment?.author?.node.name}
                                </div>
                                <span
                                    className={'text-xs text-text-secondary dark:text-white/70'}
                                    suppressHydrationWarning={true}
                                >
                                    {formatCommentDate(comment?.date || '')}
                                </span>
                            </div>
                        </div>
                    </div>
                    {broker && brokerData?.newfields?.logo?.mediaItemUrl && (
                        <Link
                            href={broker.uri || '#'}
                            className={
                                'flex items-center gap-1 rounded bg-gray-100 text-xs text-black/80 px-2 py-1 transition-all hover:-translate-y-1 dark:bg-white/10 dark:text-white'
                            }
                        >
                            <span> {brokerData?.newfields?.faName}</span>
                        </Link>
                    )}
                </div>
                {renderStars(comment?.rate || 0)}
                <div
                    className={clsx(
                        'my-3 text-justify text-[13px] leading-7 lg:text-xs lg:leading-[1.9] commentSection',
                        isLong &&
                            !expanded &&
                            '!mb-1 max-h-28 overflow-hidden [mask-image:linear-gradient(to_bottom,black_70%,transparent)]',
                    )}
                    // SECURE INJECTION: Passes the raw content through the firewall first
                    dangerouslySetInnerHTML={{ __html: safeHtmlLinks(safeContent) }}
                />
                {isLong && (
                    <button
                        type='button'
                        onClick={() => setExpanded((v) => !v)}
                        aria-expanded={expanded}
                        className='mb-2 text-xs font-medium text-progress dark:text-blue-300'
                    >
                        {expanded ? 'بستن' : 'ادامه'}
                    </button>
                )}
            </div>
            <div className={'flex justify-between'}>
                <Reply comment={comment} />
                <div className='flex flex-col items-end justify-center gap-x-2 text-xs text-text-secondary/40 dark:text-white/40'>
                    <LikeDislike
                        commentId={comment.databaseId}
                        likes={comment?.likes || 0}
                        dislikes={comment?.dislikes || 0}
                    />
                </div>
            </div>
        </>
    );
};

export default Comment;
